import { parseModuleSchema } from "@frontend/pages/case-workspace/radiology/lib/module-schema"
import type { ActiveModulePanelProps } from "@frontend/pages/case-workspace/radiology/lib/panel-types"

import { ImageLabelModulePanel } from "./image-label-module-panel"
import { MaskModulePanel } from "./mask-module-panel"
import { PiqualModulePanel } from "./piqual-module-panel"
import { PiradsModulePanel } from "./pirads-module-panel"
import { ScoreModulePanel } from "./score-module-panel"
import { TextModulePanel } from "./text-module-panel"
import { VectorModulePanel } from "./vector-module-panel"

export function ActiveModulePanel({
  activeModule,
  modules,
  activeVectorLabelKey,
  onActiveVectorLabelKey,
  activeMaskInstanceId,
  onSelectMaskInstance,
  onActivateSingleMaskLabel,
  onCreateMaskInstance,
  onDeleteMaskInstance,
  drawingDisabled,
  onDrawingDisabled,
  activeGeometryType,
  onActiveGeometryType,
  brushSize,
  onBrushSize,
  filledPen,
  onFilledPen,
  eraseMode,
  onEraseMode,
  samMode,
  onSamMode,
  samAvailable,
  samPending,
  samScribbleMode,
  onSamScribbleMode,
  samConfidence,
  onSamConfidence,
  maskOpacityById,
  onMaskOpacityChange,
  onMaskOpacityCommit,
  onMaskVisibilityToggle,
  masks,
  scores,
  imageLabels,
  texts,
  onUpsertScore,
  onUpsertImageLabel,
  onUpsertText,
  activeSeriesId,
  activeMaskSeriesId,
  availableSeries,
  isActiveModuleEditable,
  maskFilterMismatchReason
}: ActiveModulePanelProps) {
  if (!activeModule) {
    return (
      <div className="flex-1 flex items-center justify-center text-[11px] text-muted-foreground">
        No module selected
      </div>
    )
  }

  const schema = parseModuleSchema(activeModule.template_schema)
  const type = activeModule.template_type

  if (type === "mask" && schema?.type === "mask") {
    return (
      <MaskModulePanel
        schema={schema}
        moduleId={activeModule.id}
        seriesId={activeMaskSeriesId}
        masks={masks}
        activeMaskInstanceId={activeMaskInstanceId}
        onSelectMaskInstance={onSelectMaskInstance}
        onActivateSingleLabel={onActivateSingleMaskLabel}
        onCreateMaskInstance={onCreateMaskInstance}
        onDeleteMaskInstance={onDeleteMaskInstance}
        drawingDisabled={drawingDisabled}
        onDrawingDisabled={onDrawingDisabled}
        brushSize={brushSize}
        onBrushSize={onBrushSize}
        filledPen={filledPen}
        onFilledPen={onFilledPen}
        eraseMode={eraseMode}
        onEraseMode={onEraseMode}
        samMode={samMode}
        onSamMode={onSamMode}
        samAvailable={samAvailable}
        samPending={samPending}
        samScribbleMode={samScribbleMode}
        onSamScribbleMode={onSamScribbleMode}
        samConfidence={samConfidence}
        onSamConfidence={onSamConfidence}
        maskOpacityById={maskOpacityById}
        onMaskOpacityChange={onMaskOpacityChange}
        onMaskOpacityCommit={onMaskOpacityCommit}
        onMaskVisibilityToggle={onMaskVisibilityToggle}
        isEditable={isActiveModuleEditable}
        seriesFilterMismatch={maskFilterMismatchReason ?? null}
      />
    )
  }

  if (type === "score" && schema?.type === "score") {
    return (
      <ScoreModulePanel
        schema={schema}
        moduleId={activeModule.id}
        seriesId={activeSeriesId}
        scores={scores}
        onUpsertScore={onUpsertScore}
      />
    )
  }

  if (type === "vector" && schema?.type === "vector") {
    return (
      <VectorModulePanel
        schema={schema}
        activeLabelKey={activeVectorLabelKey}
        onActiveLabelKey={onActiveVectorLabelKey}
        activeGeometryType={activeGeometryType}
        onActiveGeometryType={onActiveGeometryType}
      />
    )
  }

  if (type === "image_label" && schema?.type === "image_label") {
    return (
      <ImageLabelModulePanel
        schema={schema}
        moduleId={activeModule.id}
        seriesId={schema.series_mapping ? null : activeSeriesId}
        imageLabels={imageLabels}
        onUpsertImageLabel={onUpsertImageLabel}
        availableSeries={schema.series_mapping ? availableSeries : []}
      />
    )
  }

  if (type === "text" && schema?.type === "text") {
    return (
      <TextModulePanel
        schema={schema}
        moduleId={activeModule.id}
        activeSeriesId={activeSeriesId}
        texts={texts}
        onUpsertText={onUpsertText}
      />
    )
  }

  if (type === "piqual") {
    return (
      <PiqualModulePanel
        moduleId={activeModule.id}
        seriesId={activeSeriesId}
        scores={scores}
        onUpsertScore={onUpsertScore}
        availableSeries={availableSeries}
      />
    )
  }

  if (type === "pirads") {
    return (
      <PiradsModulePanel
        moduleId={activeModule.id}
        seriesId={activeMaskSeriesId}
        modules={modules}
        masks={masks}
        scores={scores}
        onUpsertScore={onUpsertScore}
      />
    )
  }

  return (
    <div className="flex-1 flex items-center justify-center text-[11px] text-muted-foreground p-4 text-center">
      No annotation UI for "{type}"
    </div>
  )
}
